import {TableName} from "../../EditableTable/options";
import {select} from "../../../shared/SagaService";
import { CustomFieldHelper } from "../../../shared/CustomFieldHelper";
import { Type } from "../../Transaction/helper";
import { SubscriptionCheck } from "../../../components/Access/helper/subscription/SubscriptionCheck";
import { SubscriptionScopeKey } from "../../../components/Access/helper/subscription/enum/SubscriptionScopeKey";

const Common = {
    form_items: child => {
        const hasUOMSubscription = SubscriptionCheck.withKeys(SubscriptionScopeKey["PRODUCT.MULTI_UOM"]).hasSubscriptionTo();
        const hasMultiLocationSubscription = SubscriptionCheck
            .withKeys(SubscriptionScopeKey["INVENTORY.MULTI_LOCATION"]).hasSubscriptionTo();

        const hasTaxSubscription = SubscriptionCheck.withKeys(SubscriptionScopeKey['TAX.SST']).hasSubscriptionTo();

        return ({
            account_id: child.account_id,
            description: child.description,
            product_id: child.product_id || null,
            product_unit_id: !hasUOMSubscription ? undefined : (child.product_unit_id || null),
            location_id: hasMultiLocationSubscription ? child.location_id || null : undefined,
            quantity: parseFloat((child.quantity || child.quantity === 0) ? child.quantity : 1),
            unit_price: parseFloat(child.unit_price),
            discount: child.discount || null,
            tax_code_id: hasTaxSubscription ? child.tax_code_id || null : undefined,
            classification_code: child.classification_code || undefined,
        });
    },
    link_items: child => {
        if (child.target_transaction_id) {
            return {
                target_transaction_id: child.target_transaction_id,
                apply_amount: parseFloat(child.apply_amount || 0),
            };
        }
        return false;
    },
    bank_items: child => {
        const hasTaxSubscription = SubscriptionCheck.withKeys(SubscriptionScopeKey['TAX.SST']).hasSubscriptionTo();
        return ({
            account_id: child.account_id,
            description: child.description,
            amount: parseFloat(child.amount),
            tax_code_id: hasTaxSubscription ? child.tax_code_id || null : undefined,
        });
    },
    journal_items: child => ({
        account_id: child.account_id,
        description: child.description,
        debit_amount: child.debit_amount === "0.00" ? null : child.debit_amount,
        credit_amount: child.credit_amount === "0.00" ? null : child.credit_amount,
    }),
};

function prepareRequestDataWorker(
    items = [],
    parentMapFn = () => ({}),
    childKey = 'items',
    childMapFn = () => ({}),
    extra = {},
) {
    return {
        transactions: items.map(parent => ({
            ...parentMapFn(parent),
            [childKey]: [
                childMapFn(parent),
                ...parent.children.map(child => childMapFn(child)),
            ].filter(item => !!item),
        })),
        ...extra,
    };
}

export function* prepareBatchRequestData(tableName) {
    const { Editable, BatchInvoicing, List, Auth } = yield select();
    const { items } = Editable[tableName] || { items: [] };
    const {
        taxInclusive: tax_inclusive,
        status,
        myInvoisAction: myinvois_action,
    } = BatchInvoicing;

    const companyCurrencyCode = Auth?.companyCurrencyCode;
    const isMalaysianCompany = Auth?.companyCountryCode === 'MY';
    const isMyInvoisSubmissionOn = List.settings.is_myinvois_submission_enabled;

    const hasCustomFieldSub =
        SubscriptionCheck.withKeys(SubscriptionScopeKey.CUSTOM_FIELD).hasSubscriptionTo();

    const hasTagSub =
        SubscriptionCheck.withKeys(SubscriptionScopeKey.TAG).hasSubscriptionTo();

    const hasMultiCurrencySub =
        SubscriptionCheck.withKeys(SubscriptionScopeKey.MULTI_CURRENCY).hasSubscriptionTo();

    switch (tableName) {
        case TableName.batch_quotation:
            return prepareRequestDataWorker(
                items,
                parent => ({
                    contact_id: parent.contact_id,
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    title: parent.title || null,
                    description: parent.master_description || null,
                    tax_inclusive,
                    status,
                    // term_id: parent.term_id || null,
                    // due_date: parent.due_date || null,
                }),
                'items',
                Common.form_items,
            );
        case TableName.batch_sale_order:
            return prepareRequestDataWorker(
                items,
                parent => ({
                    contact_id: parent.contact_id,
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    title: parent.title || null,
                    description: parent.master_description || null,
                    tax_inclusive,
                    status,

                    // Custom Fields
                    ...(hasCustomFieldSub ?
                        CustomFieldHelper.prepareFormData(parent, Type.sale_order) : {}),
                }),
                'items',
                Common.form_items,
            );
        case TableName.batch_delivery_order:
            return prepareRequestDataWorker(
                items,
                parent => ({
                    contact_id: parent.contact_id,
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    title: parent.title || null,
                    description: parent.master_description || null,
                    tax_inclusive,
                    status,
                }),
                'items',
                Common.form_items,
            );
        case TableName.batch_invoice: {
            const parsedMyInvoisAction = status === 'ready' ? myinvois_action : null;
            return prepareRequestDataWorker(
                items,
                parent => ({
                    contact_id: parent.contact_id,
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    title: parent.title || null,
                    description: parent.master_description || null,
                    tax_inclusive,
                    term_id: parent.term_id || null,
                    due_date: parent.due_date || null,

                    // Custom Fields
                    ...(hasCustomFieldSub ?
                        CustomFieldHelper.prepareFormData(parent, Type.sale_invoice) : {}),
                }),
                'items',
                Common.form_items,
                {
                    payment_mode: 'credit',
                    myinvois_action: isMalaysianCompany && isMyInvoisSubmissionOn ?
                        parsedMyInvoisAction || null : undefined,
                    status,
                },
            );
        }
        case TableName.batch_invoice_cash: {
            const parsedMyInvoisAction = status === 'ready' ? myinvois_action : null;
            return prepareRequestDataWorker(
                items,
                parent => ({
                    contact_id: parent.contact_id,
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    title: parent.title || null,
                    description: parent.master_description || null,
                    tax_inclusive,
                    account_id: parent.payment_account_id,

                    // Custom Fields
                    ...(hasCustomFieldSub ?
                        CustomFieldHelper.prepareFormData(parent, Type.sale_invoice) : {}),
                }),
                'items',
                Common.form_items,
                {
                    payment_mode: 'cash',
                    myinvois_action: isMalaysianCompany && isMyInvoisSubmissionOn ?
                        parsedMyInvoisAction || null : undefined,
                    status,
                },
            );
        }
        case TableName.batch_invoice_credit_note: {
            const parsedMyInvoisAction = status === 'ready' ? myinvois_action : null;
            return prepareRequestDataWorker(
                items,
                parent => ({
                    contact_id: parent.contact_id,
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    title: parent.title || null,
                    description: parent.master_description || null,
                    tax_inclusive,
                    // term_id: parent.term_id || null,
                    // due_date: parent.due_date || null,
                }),
                'items',
                Common.form_items,
                {
                    myinvois_action: isMalaysianCompany && isMyInvoisSubmissionOn ?
                        parsedMyInvoisAction || null : undefined,
                    status,
                },
            );
        }
        case TableName.batch_invoice_payment:
            return prepareRequestDataWorker(
                items,
                parent => ({
                    contact_id: parent.contact_id || null,
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    amount: parseFloat(parent.amount || 0),
                    fee_text: parent.fee_text || null,
                    fee_account_id: parent.fee_text ? parent.fee_account_id || null : null,
                    account_id: parent.account_id,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    title: parent.title || null,
                    description: parent.master_description || null,
                    status,
                    payment_method_id: parent.payment_method,
                }),
                'link_items',
                Common.link_items,
            );
        case TableName.batch_sale_refund:
            return prepareRequestDataWorker(
                items,
                parent => ({
                    contact_id: parent.contact_id || null,
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    amount: parseFloat(parent.amount || 0),
                    account_id: parent.account_id,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    title: parent.title || null,
                    description: parent.master_description || null,
                    status,
                }),
                'link_items',
                Common.link_items,
            );
        case TableName.batch_purchase_order:
            return prepareRequestDataWorker(
                items,
                parent => ({
                    contact_id: parent.contact_id || null,
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    title: parent.title || null,
                    description: parent.master_description || null,
                    tax_inclusive,
                    status,
                }),
                'items',
                Common.form_items,
            );
        case TableName.batch_bill: {
            const parsedMyInvoisAction = status === 'ready' ? myinvois_action : null;
            return prepareRequestDataWorker(
                items,
                parent => ({
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    description: parent.master_description || null,
                    tax_inclusive,
                    contact_id: parent.contact_id || null,
                    term_id: parent.term_id || null,
                    due_date: parent.due_date || null,
                }),
                'items',
                Common.form_items,
                {
                    payment_mode: 'credit',
                    myinvois_action: isMalaysianCompany && isMyInvoisSubmissionOn ?
                        parsedMyInvoisAction || null : undefined,
                    status,
                },
            );
        }
        case TableName.batch_bill_cash: {
            const parsedMyInvoisAction = status === 'ready' ? myinvois_action : null;
            return prepareRequestDataWorker(
                items,
                parent => ({
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    description: parent.master_description || null,
                    tax_inclusive,
                    contact_id: parent.contact_id || null,
                    account_id: parent.payment_account_id,
                }),
                'items',
                Common.form_items,
                {
                    payment_mode: 'cash',
                    myinvois_action: isMalaysianCompany && isMyInvoisSubmissionOn ?
                        parsedMyInvoisAction || null : undefined,
                    status,
                },
            );
        }
        case TableName.batch_bill_expense_claim: {
            const parsedMyInvoisAction = status === 'ready' ? myinvois_action : null;
            return prepareRequestDataWorker(
                items,
                parent => ({
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    description: parent.master_description || null,
                    tax_inclusive,
                    contact_id: parent.contact_id || null,
                    contact2_id: parent.contact2_id || null,
                    term_id: parent.term_id || null,
                    due_date: parent.due_date || null,
                }),
                'items',
                Common.form_items,
                {
                    payment_mode: 'claim',
                    myinvois_action: isMalaysianCompany && isMyInvoisSubmissionOn ?
                        parsedMyInvoisAction || null : undefined,
                    status,
                },
            );
        }
        case TableName.batch_purchase_credit_note: {
            const parsedMyInvoisAction = status === 'ready' ? myinvois_action : null;
            return prepareRequestDataWorker(
                items,
                parent => ({
                    contact_id: parent.contact_id,
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    title: parent.title || null,
                    description: parent.master_description || null,
                    tax_inclusive,
                    // term_id: parent.term_id || null,
                    // due_date: parent.due_date || null,
                }),
                'items',
                Common.form_items,
                {
                    myinvois_action: isMalaysianCompany && isMyInvoisSubmissionOn ?
                        parsedMyInvoisAction || null : undefined,
                    status,
                },
            );
        }
        case TableName.batch_bill_payment:
            return prepareRequestDataWorker(
                items,
                parent => ({
                    contact_id: parent.contact_id || null,
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    amount: parseFloat(parent.amount || 0),
                    fee_text: parent.fee_text || null,
                    fee_account_id: parent.fee_text ? parent.fee_account_id || null : null,
                    account_id: parent.account_id,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    title: parent.title || null,
                    description: parent.master_description || null,
                    status,
                    payment_method_id: parent.payment_method,
                }),
                'link_items',
                Common.link_items,
            );
        case TableName.batch_bank_income:
            return prepareRequestDataWorker(
                items,
                parent => ({
                    contact_id: parent.contact_id || null,
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    tax_inclusive,
                    title: parent.title || null,
                    description: parent.master_description || null,
                    account_id: parent.bank_account_id,
                    status,

                    // Custom Fields
                    ...CustomFieldHelper.prepareFormData(parent, Type.bank_income),
                }),
                'items',
                Common.bank_items,
            );
        case TableName.batch_bank_expense:
            return prepareRequestDataWorker(
                items,
                parent => ({
                    contact_id: parent.contact_id || null,
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    title: parent.title || null,
                    description: parent.master_description || null,
                    tax_inclusive,
                    account_id: parent.bank_account_id,
                    status,

                    // Custom Fields
                    ...CustomFieldHelper.prepareFormData(parent, Type.bank_expense),
                }),
                'items',
                Common.bank_items,
            );
        case TableName.batch_journal_entry:
            return prepareRequestDataWorker(
                items,
                parent => ({
                    number: parent.number || null,
                    number2: parent.number2 || null,
                    date: parent.date,
                    currency_code: hasMultiCurrencySub ? parent.currency_code : companyCurrencyCode,
                    exchange_rate: hasMultiCurrencySub ? parent.exchange_rate : 1,
                    tag_ids: hasTagSub ? parent.tag_ids || null : undefined,
                    title: parent.title || null,
                    description: parent.master_description || null,
                    status,
                }),
                'items',
                Common.journal_items,
            );
        default:
            return null;
    }
}
